// structuralDifferentialEvolution.ts
// Forks the neural network into parallel "possibility branches," simulates each
// independently for a short window, compares fitness, and reinforces the winner.
// This enables the network to explore divergent evolutionary paths simultaneously.

import { NeuralNetworkSimulator } from '../NeuralNetworkSimulator';
import { Node, Edge } from '../types';

// ── Branch Types ──

export interface EvolutionBranch {
	id: string;
	label: string;
	strategy: BranchStrategy;
	nodes: Node[];         // Snapshot of node state for this branch
	edges: Edge[];         // Snapshot of edge state
	fitness: BranchFitness;
	createdAtStep: number;
	lastEvaluatedStep: number;
	active: boolean;
	parentBranchId?: string;
}

export interface BranchFitness {
	stabilityScore: number;    // 0-1: how stable the configuration is
	energyScore: number;       // 0-1: average energy across nodes
	diversityScore: number;    // 0-1: node color/value diversity
	connectivityScore: number; // 0-1: edge density quality
	noveltyScore: number;      // 0-1: how different from parent
	compositeScore: number;    // 0-1: weighted aggregate
}

export type BranchStrategy =
	| 'high-energy'       // Favor high-energy node configurations
	| 'dense-connect'     // Maximize edge connectivity
	| 'diverse-spectrum'  // Maximize value/color diversity
	| 'stable-equilibrium' // Minimize node velocity/turbulence
	| 'exploratory'       // Random mutation for novelty
	| 'ancestral'         // Closest to original network state
	| 'hybrid';           // Blend of parent branches

// ── Configuration ──

const MAX_ACTIVE_BRANCHES = 6;       // Hard cap on parallel branches
const BRANCH_EVAL_INTERVAL = 180;     // Steps between evaluations
const BRANCH_PRUNE_THRESHOLD = 0.15;  // Minimum composite fitness to survive
const NOVELTY_DECAY = 0.95;          // Novelty score decay per evaluation

// ── Engine ──

export class StructuralDifferentialEvolution {
	private sim: NeuralNetworkSimulator;
	private branches: Map<string, EvolutionBranch> = new Map();
	private branchCounter: number = 0;
	private lastPruneStep: number = 0;

	constructor(sim: NeuralNetworkSimulator) {
		this.sim = sim;
	}

	/** Main evolution tick — called from cycle() */
	evolveBranches(): void {
		const step = this.sim.step;

		// 1. If we have few branches, fork new ones from current network state
		if (this.branches.size < 2 && this.sim.nodes.length >= 10) {
			this.forkNewBranches(2);
		}

		// 2. Evaluate all active branches
		for (const branch of this.branches.values()) {
			if (!branch.active) continue;
			branch.fitness = this.evaluateFitness(branch);
			branch.lastEvaluatedStep = step;
		}

		// 3. Prune low-fitness branches periodically
		if (step - this.lastPruneStep >= BRANCH_EVAL_INTERVAL) {
			this.pruneWeakBranches();
			this.lastPruneStep = step;

			// 4. Reinforce: apply best branch's traits to main network
			const best = this.getBestBranch();
			if (best) {
				this.reinforceMainNetwork(best);
			}
		}

		// 5. Fork new branches from the best branch (adaptive exploration)
		if (this.branches.size < MAX_ACTIVE_BRANCHES && step % (BRANCH_EVAL_INTERVAL * 2) === 0) {
			const best = this.getBestBranch();
			if (best && best.fitness.compositeScore > 0.6) {
				const newCount = Math.min(2, MAX_ACTIVE_BRANCHES - this.branches.size);
				this.forkFromBranch(best, newCount);
			}
		}
	}

	/** Fork new branches from current network state using diverse strategies */
	private forkNewBranches(count: number): void {
		const strategies: BranchStrategy[] = [
			'high-energy', 'dense-connect', 'diverse-spectrum',
			'stable-equilibrium', 'exploratory', 'ancestral'
		];

		const snapshot = this.snapshotNetwork();
		for (let i = 0; i < count; i++) {
			const strategy = strategies[this.branchCounter % strategies.length];
			const id = `branch_${this.branchCounter++}_${strategy}`;
			const branch: EvolutionBranch = {
				id,
				label: `${strategy} #${this.branchCounter}`,
				strategy,
				nodes: this.cloneNodes(snapshot.nodes),
				edges: this.cloneEdges(snapshot.edges),
				fitness: this.emptyFitness(),
				createdAtStep: this.sim.step,
				lastEvaluatedStep: this.sim.step,
				active: true,
			};
			this.applyStrategy(branch);
			this.branches.set(id, branch);
		}
	}

	/** Fork new branches from an existing branch */
	private forkFromBranch(parent: EvolutionBranch, count: number): void {
		const strategies: BranchStrategy[] = ['hybrid', 'exploratory', 'diverse-spectrum'];
		for (let i = 0; i < count; i++) {
			const strategy = strategies[i % strategies.length];
			const id = `branch_${this.branchCounter++}_${strategy}`;
			const branch: EvolutionBranch = {
				id,
				label: `${strategy} ← ${parent.label}`,
				strategy,
				nodes: this.cloneNodes(parent.nodes),
				edges: this.cloneEdges(parent.edges),
				fitness: this.emptyFitness(),
				createdAtStep: this.sim.step,
				lastEvaluatedStep: this.sim.step,
				active: true,
				parentBranchId: parent.id,
			};
			this.mutateBranch(branch, strategy);
			this.branches.set(id, branch);
		}
	}

	/** Apply a strategy's initial transformation to a branch */
	private applyStrategy(branch: EvolutionBranch): void {
		switch (branch.strategy) {
			case 'high-energy':
				for (const n of branch.nodes) {
					n.energy = Math.min(1, (n.energy || 0.5) + Math.random() * 0.3);
				}
				break;
			case 'dense-connect': {
				const existing = new Set(branch.edges.map(e => `${e.from}-${e.to}`));
				for (let k = 0; k < Math.min(5, branch.nodes.length); k++) {
					const a = Math.floor(Math.random() * branch.nodes.length);
					const b = Math.floor(Math.random() * branch.nodes.length);
					if (a !== b && !existing.has(`${a}-${b}`) && !existing.has(`${b}-${a}`)) {
						branch.edges.push({ from: branch.nodes[a].id, to: branch.nodes[b].id, weight: 0.3 + Math.random() * 0.4 });
						existing.add(`${a}-${b}`);
					}
				}
				break;
			}
			case 'diverse-spectrum':
				for (const n of branch.nodes) {
					n.value = Math.random();
					n.color = `hsl(${Math.floor(n.value * 360)}, 70%, 60%)`;
				}
				break;
			case 'stable-equilibrium':
				for (const n of branch.nodes) {
					n.vx = (n.vx || 0) * 0.3;
					n.vy = (n.vy || 0) * 0.3;
					n.vz = (n.vz || 0) * 0.3;
				}
				break;
			case 'exploratory':
				this.mutateBranch(branch, 'exploratory');
				break;
			case 'ancestral':
				// No mutation — keep as original snapshot
				break;
			case 'hybrid':
				break; // Handled in forkFromBranch
		}
	}

	/** Mutate a branch's nodes (position jitter + energy change) */
	private mutateBranch(branch: EvolutionBranch, _strategy: BranchStrategy): void {
		for (const n of branch.nodes) {
			n.x += (Math.random() - 0.5) * 20;
			n.y += (Math.random() - 0.5) * 20;
			n.z = (n.z || 0) + (Math.random() - 0.5) * 15;
			n.energy = Math.max(0, Math.min(1, (n.energy || 0.5) + (Math.random() - 0.5) * 0.2));
			n.value = Math.max(0, Math.min(1, (n.value || 0.5) + (Math.random() - 0.5) * 0.1));
		}
		// Randomly add/remove edges
		if (Math.random() < 0.3 && branch.nodes.length > 1) {
			const a = branch.nodes[Math.floor(Math.random() * branch.nodes.length)];
			const b = branch.nodes[Math.floor(Math.random() * branch.nodes.length)];
			if (a.id !== b.id) {
				const exists = branch.edges.some(e =>
					(e.from === a.id && e.to === b.id) || (e.from === b.id && e.to === a.id));
				if (!exists) {
					branch.edges.push({ from: a.id, to: b.id, weight: Math.random() });
				}
			}
		}
	}

	/** Evaluate the fitness of a branch snapshot */
	private evaluateFitness(branch: EvolutionBranch): BranchFitness {
		const nodes = branch.nodes;
		const edges = branch.edges;
		const n = nodes.length;

		if (n === 0) return this.emptyFitness();

		// Stability: inverse of average velocity magnitude
		let totalVel = 0;
		for (const node of nodes) {
			const vx = node.vx || 0;
			const vy = node.vy || 0;
			const vz = node.vz || 0;
			totalVel += Math.sqrt(vx * vx + vy * vy + vz * vz);
		}
		const stabilityScore = Math.max(0, 1 - (totalVel / n) * 0.5);

		// Energy: average node energy
		let totalEnergy = 0;
		for (const node of nodes) {
			totalEnergy += node.energy || 0;
		}
		const energyScore = totalEnergy / n;

		// Diversity: standard deviation of node values
		let totalVal = 0;
		for (const node of nodes) {
			totalVal += node.value || 0;
		}
		const meanVal = totalVal / n;
		let varVal = 0;
		for (const node of nodes) {
			const d = (node.value || 0) - meanVal;
			varVal += d * d;
		}
		const diversityScore = Math.min(1, Math.sqrt(varVal / n) * 3);

		// Connectivity: edge-to-node ratio (ideal ~1.5-2.5)
		const ratio = n > 0 ? edges.length / n : 0;
		const connectivityScore = Math.max(0, 1 - Math.abs(ratio - 2) * 0.5);

		// Novelty: decays over time
		const stepsSinceCreation = this.sim.step - branch.createdAtStep;
		const noveltyScore = Math.exp(-stepsSinceCreation * 0.001) * (branch.parentBranchId ? 0.8 : 1.0);

		// Composite: weighted blend
		const compositeScore =
			stabilityScore * 0.25 +
			energyScore * 0.20 +
			diversityScore * 0.20 +
			connectivityScore * 0.15 +
			noveltyScore * 0.20;

		return {
			stabilityScore: Math.round(stabilityScore * 1000) / 1000,
			energyScore: Math.round(energyScore * 1000) / 1000,
			diversityScore: Math.round(diversityScore * 1000) / 1000,
			connectivityScore: Math.round(connectivityScore * 1000) / 1000,
			noveltyScore: Math.round(noveltyScore * 1000) / 1000,
			compositeScore: Math.round(compositeScore * 1000) / 1000,
		};
	}

	/** Prune branches below the fitness threshold (keep at least 2) */
	private pruneWeakBranches(): void {
		const active: EvolutionBranch[] = [];
		for (const b of this.branches.values()) {
			if (b.active) active.push(b);
		}
		if (active.length <= 2) return;

		// Sort by composite fitness ascending (weakest first)
		active.sort((a, b) => a.fitness.compositeScore - b.fitness.compositeScore);

		// Mark weakest for deactivation, but keep at least 2
		const toPrune = Math.min(active.length - 2, active.length);
		for (let i = 0; i < toPrune; i++) {
			if (active[i].fitness.compositeScore < BRANCH_PRUNE_THRESHOLD) {
				const branch = this.branches.get(active[i].id);
				if (branch) branch.active = false;
			}
		}

		// Clean up fully inactive branches
		if (this.branches.size > MAX_ACTIVE_BRANCHES * 2) {
			for (const [id, branch] of this.branches) {
				if (!branch.active) this.branches.delete(id);
			}
		}
	}

	/** Get the best (highest composite fitness) active branch */
	private getBestBranch(): EvolutionBranch | null {
		let best: EvolutionBranch | null = null;
		let bestScore = -Infinity;
		for (const branch of this.branches.values()) {
			if (!branch.active) continue;
			if (branch.fitness.compositeScore > bestScore) {
				bestScore = branch.fitness.compositeScore;
				best = branch;
			}
		}
		return best;
	}

	/** Reinforce main network with traits from the best branch */
	private reinforceMainNetwork(best: EvolutionBranch): void {
		const alpha = 0.08; // Blending factor — small to avoid disruption
		const mainNodes = this.sim.nodes;

		for (let i = 0; i < Math.min(best.nodes.length, mainNodes.length); i++) {
			const bn = best.nodes[i];
			const mn = mainNodes[i];
			if (!mn || mn.corroded) continue;

			// Interpolate energy toward best branch's energy
			mn.energy = mn.energy * (1 - alpha) + (bn.energy || 0.5) * alpha;
			// Nudge value toward best branch's value
			mn.value = mn.value * (1 - alpha * 0.5) + (bn.value || 0.5) * alpha * 0.5;
		}
	}

	/** Snapshot current network state (lightweight copy) */
	private snapshotNetwork(): { nodes: Node[]; edges: Edge[] } {
		return {
			nodes: this.cloneNodes(this.sim.nodes),
			edges: this.cloneEdges(this.sim.edges),
		};
	}

	/** Shallow clone nodes (copies position/energy/value, strips functions) */
	private cloneNodes(nodes: Node[]): Node[] {
		return nodes.map(n => ({
			id: n.id,
			x: n.x,
			y: n.y,
			z: n.z ?? 0,
			value: n.value ?? 0.5,
			energy: n.energy ?? 0.5,
			color: n.color ?? '#888',
			vx: n.vx ?? 0,
			vy: n.vy ?? 0,
			vz: n.vz ?? 0,
			corroded: n.corroded ?? false,
			nodeType: n.nodeType,
		}));
	}

	/** Shallow clone edges */
	private cloneEdges(edges: Edge[]): Edge[] {
		return edges.map(e => ({
			from: e.from,
			to: e.to,
			weight: e.weight ?? 0.5,
		}));
	}

	/** Empty fitness value */
	private emptyFitness(): BranchFitness {
		return {
			stabilityScore: 0,
			energyScore: 0,
			diversityScore: 0,
			connectivityScore: 0,
			noveltyScore: 0,
			compositeScore: 0,
		};
	}

	// ── Public API ──

	/** Get list of active branches (for UI/render) */
	getActiveBranches(): EvolutionBranch[] {
		const active: EvolutionBranch[] = [];
		for (const b of this.branches.values()) {
			if (b.active) active.push(b);
		}
		return active.sort((a, b) => b.fitness.compositeScore - a.fitness.compositeScore);
	}

	/** Get branch count */
	getBranchCount(): number {
		let count = 0;
		for (const b of this.branches.values()) {
			if (b.active) count++;
		}
		return count;
	}

	/** Get best branch fitness summary */
	getBestFitness(): BranchFitness | null {
		const best = this.getBestBranch();
		return best ? best.fitness : null;
	}
}
